import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  className = '',
  id,
  'aria-describedby': describedByProp,
  ...props
}, ref) => {
  // useId fallback keeps label/error associations working for inputs rendered
  // without a label or explicit id.
  const generatedId = useId();
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-') || generatedId;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;
  // The message the field is described by — the error replaces the helper, and
  // role="alert" below announces it when it appears (JAR-289). A caller's own
  // description sits beside it rather than being dropped (JAR-2717).
  const describedBy = [error ? errorId : helperText ? helperId : undefined, describedByProp].filter(Boolean).join(' ') || undefined;

  // iPhone Safari gives `<input type="date">` its own wide minimum width and
  // ignores `w-full`, so in a two-column row the second date runs past the edge
  // and over the first. `appearance-none` lets it honour its width, `min-w-0`
  // lets the grid or flex item shrink below that intrinsic minimum, and the
  // wrapper needs min-w-0 for the same reason the column does. Chromium's date
  // input is narrow enough to hide all of this, which is why it came back three
  // times: JAR-2468, JAR-2609, and JAR-2625, where it moved here so that every
  // date field in the app gets it rather than each screen remembering.
  const isDate = props.type === 'date';

  return (
    <div className={`w-full${isDate ? ' min-w-0' : ''}`}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-ink mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">
            {leftIcon}
          </div>
        )}
        <input
          {...props}
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`
            w-full px-4 py-3 rounded-xl border bg-surface text-ink
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
            placeholder:text-muted
            ${leftIcon ? 'pl-10' : ''}
            ${rightIcon ? 'pr-10' : ''}
            ${error ? 'border-danger focus:ring-danger' : 'border-border hover:border-muted'}
            ${isDate ? 'min-w-0 appearance-none' : ''}
            ${className}
          `}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted">
            {rightIcon}
          </div>
        )}
      </div>
      {error && <p key={error} id={errorId} role="alert" className="mt-1.5 text-sm text-danger">{error}</p>}
      {helperText && !error && <p id={helperId} className="mt-1.5 text-sm text-muted">{helperText}</p>}
    </div>
  );
});

Input.displayName = 'Input';
